"use client";

import { EditFileMetadataDialog } from "@/components/edit-file-metadata-dialog";
import { PageHeader } from "@/components/page-header";
import { CollectionGrid } from "@/components/knowledge-base/collection-grid";
import {
  AddTextDialog,
  BulkMoveCollectionDialog,
  BulkMoveTeamDialog,
  ChunksDialog,
  CreateCollectionDialog,
  MoveTeamDialog,
  RenameCollectionDialog,
} from "@/components/knowledge-base/kb-dialogs";
import {
  KbFilesTable,
  type KbSort,
  type KbSortKey,
} from "@/components/knowledge-base/kb-files-table";
import { KbRowActions, type KbMoveTargets } from "@/components/knowledge-base/kb-row-actions";
import { KbStatusBar, type KbStatusFilter } from "@/components/knowledge-base/kb-status-bar";
import {
  countStatuses,
  flattenTeamFiles,
  groupFileRows,
  pluralise,
  toKbStatus,
  type KbDragPayload,
} from "@/components/knowledge-base/kb-utils";
import { TeamTabs } from "@/components/knowledge-base/team-tabs";
import { LearnButton } from "@/components/learn-button";
import { ProjectFilePreviewPanel } from "@/components/project-file-preview-panel";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { DeleteIcon } from "@/components/ui/delete";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileTextIcon } from "@/components/ui/file-text";
import { MessageSquarePlusIcon } from "@/components/ui/message-square-plus";
import { RefreshCWIcon } from "@/components/ui/refresh-cw";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UploadIcon } from "@/components/ui/upload";
import {
  useFileManagerTree,
  type FileManagerTreeNode,
} from "@/hooks/use-file-manager-tree";
import { useKnowledgeUpload } from "@/hooks/use-knowledge-upload";
import { useProjectFilePreview } from "@/hooks/use-project-file-preview";
import { getParser } from "@/lib/file-parser";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { DbTeam } from "@/types/db";
import { FolderInput, FolderOpenIcon, MoreHorizontalIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const nameCollator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

type FilesPanelProps = {
  teams: DbTeam[];
  teamsLoading: boolean;
  activeTeamId: string | null;
  currentUserId: string | null;
  /** Elevated roles may request team moves for files they didn't upload. */
  userRole?: string | null;
  onBack?: () => void;
  onLearn?: () => void;
};

export function FilesPanel({
  teams,
  teamsLoading,
  activeTeamId,
  currentUserId,
  userRole,
  onBack,
  onLearn,
}: FilesPanelProps) {
  const router = useRouter();
  const {
    nodes,
    nodeIndex,
    loading,
    error,
    refresh,
    refreshStatuses,
  } = useFileManagerTree(teams);
  const { uploadFile } = useKnowledgeUpload();
  const { preview, previewingFileId, openPreview, closePreview } = useProjectFilePreview();

  // ─── Selection & filters ─────────────────────────────────────────────────────

  const [selectedTeamNodeId, setSelectedTeamNodeId] = useState<string | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<KbStatusFilter>("all");
  const [sort, setSort] = useState<KbSort | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());

  const activeTeamNodeId = activeTeamId ? `team:${activeTeamId}` : undefined;
  const resolvedTeamNodeId =
    selectedTeamNodeId && nodeIndex.has(selectedTeamNodeId)
      ? selectedTeamNodeId
      : activeTeamNodeId && nodeIndex.has(activeTeamNodeId)
        ? activeTeamNodeId
        : nodes[0]?.id ?? null;

  const selectedTeam = resolvedTeamNodeId ? nodeIndex.get(resolvedTeamNodeId) ?? null : null;
  const collections = useMemo(
    () => (selectedTeam?.children ?? []).filter((node) => node.kind === "collection"),
    [selectedTeam]
  );
  const selectedCollection =
    selectedCollectionId && nodeIndex.has(selectedCollectionId)
      ? nodeIndex.get(selectedCollectionId) ?? null
      : null;

  // Reset the per-team view whenever the team changes.
  useEffect(() => {
    setSelectedFileIds(new Set());
    setSelectedCollectionId(null);
    setStatusFilter("all");
    setSort(null);
    setExpandedGroups(new Set());
  }, [resolvedTeamNodeId]);

  // Opening or leaving a collection is navigation, so it starts a fresh view.
  useEffect(() => {
    setSelectedFileIds(new Set());
    setStatusFilter("all");
    setExpandedGroups(new Set());
  }, [selectedCollectionId]);

  const openCollection = useCallback((collection: FileManagerTreeNode) => {
    setSearch("");
    setSelectedCollectionId(collection.id);
  }, []);

  // ─── Derived file lists ──────────────────────────────────────────────────────

  const trimmedSearch = search.trim().toLowerCase();
  const isSearching = trimmedSearch.length > 0;

  // Inside a collection: its files. At the team root: only files that aren't in
  // a collection (collections are shown as folders above) — except while
  // searching, which covers the whole team so nothing is hard to find.
  const teamFiles = useMemo(() => flattenTeamFiles(selectedTeam), [selectedTeam]);
  const scopedFiles = useMemo(() => {
    if (selectedCollection) {
      return teamFiles.filter((file) => file.collectionId === selectedCollection.collectionId);
    }
    return isSearching ? teamFiles : (selectedTeam?.directFiles ?? []);
  }, [teamFiles, selectedCollection, selectedTeam, isSearching]);
  const statusCounts = useMemo(() => countStatuses(scopedFiles), [scopedFiles]);

  const visibleFiles = useMemo(
    () =>
      scopedFiles.filter((file) => {
        if (statusFilter !== "all" && toKbStatus(file.processingStatus) !== statusFilter) {
          return false;
        }
        return trimmedSearch ? file.name.toLowerCase().includes(trimmedSearch) : true;
      }),
    [scopedFiles, statusFilter, trimmedSearch]
  );

  const sortedFiles = useMemo(() => {
    if (!sort) return visibleFiles;
    const direction = sort.direction === "asc" ? 1 : -1;
    return [...visibleFiles].sort((left, right) => {
      if (sort.key === "size") {
        return ((left.size ?? 0) - (right.size ?? 0)) * direction;
      }
      return nameCollator.compare(left.name, right.name) * direction;
    });
  }, [visibleFiles, sort]);

  const handleSortChange = useCallback((key: KbSortKey) => {
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  }, []);

  // Grouping hides copies behind a collapsed row, which would swallow matches
  // while the list is being narrowed — so it only applies to the unfiltered view.
  const groupingEnabled = statusFilter === "all" && trimmedSearch.length === 0;
  const rows = useMemo(
    () => groupFileRows(sortedFiles, groupingEnabled),
    [sortedFiles, groupingEnabled]
  );

  // ─── Status polling ──────────────────────────────────────────────────────────

  const refreshStatusesRef = useRef(refreshStatuses);
  refreshStatusesRef.current = refreshStatuses;
  const hasInFlight = useMemo(
    () =>
      [...nodeIndex.values()].some(
        (node) =>
          node.kind === "file" &&
          (node.processingStatus?.toUpperCase() === "PENDING" ||
            node.processingStatus?.toUpperCase() === "PROCESSING")
      ),
    [nodeIndex]
  );
  useEffect(() => {
    if (!hasInFlight) return;
    const id = setInterval(() => { void refreshStatusesRef.current(); }, 3000);
    return () => clearInterval(id);
  }, [hasInFlight]);

  // ─── Upload / add text ───────────────────────────────────────────────────────

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingExternal, setIsDraggingExternal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [addTextOpen, setAddTextOpen] = useState(false);
  const dragCounterRef = useRef(0);

  // A selected collection is the upload destination; otherwise the team root.
  const uploadTarget = selectedCollection ?? selectedTeam;

  const handleFiles = useCallback(
    async (fileList: FileList | File[]) => {
      if (!currentUserId || !uploadTarget) return;

      setIsUploading(true);
      const files = Array.from(fileList);
      const errors: string[] = [];
      const targetKind = uploadTarget.kind;
      const targetEntityId = uploadTarget.entityId;

      for (const file of files) {
        try {
          // 1-3, 5: upload to storage, create the file record, kick off parsing + embedding
          const fileRecord = await uploadFile(file, currentUserId);

          // 4. Link file to the target — team or collection
          if (targetKind === "team") {
            const { error: linkError } = await supabase
              .from("file_teams")
              .insert({ user_id: currentUserId, team_id: targetEntityId, file_id: fileRecord.id });
            if (linkError) throw new Error(linkError.message);
          } else {
            const { error: linkError } = await supabase
              .from("collection_files")
              .insert({ user_id: currentUserId, collection_id: targetEntityId, file_id: fileRecord.id });
            if (linkError) throw new Error(linkError.message);
          }
        } catch (err) {
          errors.push(`${file.name}: ${err instanceof Error ? err.message : "Upload failed"}`);
        }
      }

      setIsUploading(false);

      const succeeded = files.length - errors.length;
      if (succeeded > 0) {
        toast.success(`${pluralise(succeeded, "file")} uploaded and queued for processing.`);
        void refresh();
      }
      errors.forEach((message) => toast.error(message));
    },
    [currentUserId, uploadTarget, refresh, uploadFile]
  );

  const handleAddText = useCallback(
    (title: string, content: string) => {
      const fileName = title.endsWith(".md") ? title : `${title}.md`;
      const file = new File([content], fileName, { type: "text/markdown" });
      const transfer = new DataTransfer();
      transfer.items.add(file);
      void handleFiles(transfer.files).finally(() => setAddTextOpen(false));
    },
    [handleFiles]
  );

  // ─── File actions ────────────────────────────────────────────────────────────

  const [chunksNode, setChunksNode] = useState<FileManagerTreeNode | null>(null);
  const [editMetadataNode, setEditMetadataNode] = useState<FileManagerTreeNode | null>(null);
  const [renameNode, setRenameNode] = useState<FileManagerTreeNode | null>(null);
  const [moveRequestNode, setMoveRequestNode] = useState<FileManagerTreeNode | null>(null);
  const [createCollectionOpen, setCreateCollectionOpen] = useState(false);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkMoveCollectionOpen, setBulkMoveCollectionOpen] = useState(false);
  const [retryingFailed, setRetryingFailed] = useState(false);

  const handleDeleteNode = useCallback(
    async (node: FileManagerTreeNode) => {
      if (node.kind !== "file" && node.kind !== "collection") return;
      if (!window.confirm(`Delete "${node.name}"? This cannot be undone.`)) return;

      const table = node.kind === "file" ? "files" : "collections";
      const { data: deleted, error: delError } = await supabase
        .from(table)
        .delete()
        .eq("id", node.entityId)
        .select("id");
      if (delError) { toast.error(delError.message); return; }
      // RLS silently filters rows the caller can't delete — no error, just 0 rows.
      if (!deleted?.length) {
        toast.error(`You don't have permission to delete "${node.name}".`);
        return;
      }

      toast.success(`"${node.name}" deleted.`);
      if (selectedCollectionId === node.id) setSelectedCollectionId(null);
      void refresh();
    },
    [refresh, selectedCollectionId]
  );

  const retryFile = useCallback(async (node: FileManagerTreeNode) => {
    const { error: retryError } = await supabase.functions.invoke("woods-knowledge-upload", {
      body: {
        fileId: node.entityId,
        parser: getParser(node.fileType, node.name),
        embedding_provider: "voyage",
        embedding_model: "voyage-context-3",
      },
    });
    if (retryError) throw retryError;
  }, []);

  const handleRetryNode = useCallback(
    async (node: FileManagerTreeNode) => {
      if (node.kind !== "file") return;
      try {
        await retryFile(node);
        toast.success(`"${node.name}" queued for reprocessing.`);
        void refresh();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Retry failed.");
      }
    },
    [refresh, retryFile]
  );

  const handleRetryFailed = useCallback(async () => {
    const failed = scopedFiles.filter((file) => toKbStatus(file.processingStatus) === "failed");
    if (failed.length === 0) return;
    setRetryingFailed(true);
    let failures = 0;
    for (const file of failed) {
      try {
        await retryFile(file);
      } catch {
        failures += 1;
      }
    }
    setRetryingFailed(false);
    if (failures === 0) {
      toast.success(`${pluralise(failed.length, "file")} queued for reprocessing.`);
    } else {
      toast.warning(`${failed.length - failures} of ${failed.length} files queued for reprocessing.`);
    }
    void refresh();
  }, [scopedFiles, retryFile, refresh]);

  const handlePreviewNode = useCallback(
    (node: FileManagerTreeNode) => {
      if (node.kind !== "file") return;
      void openPreview(node.entityId, node.name);
    },
    [openPreview]
  );

  const handleDownloadNode = useCallback(async (node: FileManagerTreeNode) => {
    if (node.kind !== "file") return;
    try {
      const { data: fileRecord, error: fetchError } = await supabase
        .from("files")
        .select("file_path")
        .eq("id", node.entityId)
        .single();
      if (fetchError || !fileRecord?.file_path) {
        throw new Error(fetchError?.message ?? "File path not found");
      }

      const { data: signedData, error: signError } = await supabase.storage
        .from("files")
        .createSignedUrl(fileRecord.file_path, 3600, { download: node.name });
      if (signError || !signedData?.signedUrl) {
        throw new Error(signError?.message ?? "Could not create download link");
      }

      window.open(signedData.signedUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed.");
    }
  }, []);

  const handleCreateCollection = useCallback(
    async (name: string) => {
      if (!currentUserId || !selectedTeam) return;

      const { data: collection, error: colError } = await supabase
        .from("collections")
        .insert({ name, description: "", sharing: "private", user_id: currentUserId })
        .select("id")
        .single();

      if (colError || !collection) {
        toast.error(colError?.message ?? "Failed to create collection.");
        return;
      }

      const { error: linkError } = await supabase
        .from("collection_teams")
        .insert({ team_id: selectedTeam.entityId, collection_id: collection.id, user_id: currentUserId });

      if (linkError) { toast.error(linkError.message); return; }

      toast.success(`"${name}" created.`);
      void refresh();
    },
    [currentUserId, selectedTeam, refresh]
  );

  const handleRenameCollection = useCallback(
    async (node: FileManagerTreeNode, name: string) => {
      const { error: renameError } = await supabase
        .from("collections")
        .update({ name })
        .eq("id", node.entityId);
      if (renameError) { toast.error(renameError.message); return; }
      toast.success(`Renamed to “${name}”.`);
      void refresh();
    },
    [refresh]
  );

  const canEditFileMetadata = useCallback(
    (node: FileManagerTreeNode) =>
      node.kind === "file" &&
      !!currentUserId &&
      (node.ownerId === currentUserId ||
        teams.some((team) => team.id === node.teamId && team.user_id === currentUserId)),
    [currentUserId, teams]
  );

  // Matches create_file_team_request's own guard: owner, admin, or superadmin.
  const canMoveTeam = useCallback(
    (node: FileManagerTreeNode) =>
      !!currentUserId &&
      (node.ownerId === currentUserId || userRole === "admin" || userRole === "superadmin"),
    [currentUserId, userRole]
  );

  // Mirrors files_delete_policy: the uploader, the team creator, or a team
  // member with admin/superadmin role (check_resource_permissions 'edit').
  const canDeleteFile = useCallback(
    (node: FileManagerTreeNode) => {
      if (node.kind !== "file" || !currentUserId) return false;
      if (node.ownerId === currentUserId) return true;
      const team = teams.find((t) => t.id === node.teamId);
      if (!team) return false;
      return (
        team.user_id === currentUserId ||
        team.currentUserRole === "admin" ||
        team.currentUserRole === "superadmin"
      );
    },
    [currentUserId, teams]
  );

  // ─── Cross-team moves (approval-gated) ───────────────────────────────────────

  /**
   * Cross-team moves are not immediate: the RPC files a request that the target
   * team's owner has to approve, so nothing moves here and "Undo" withdraws the
   * pending request rather than reversing a move.
   */
  const requestTeamMove = useCallback(
    async (node: FileManagerTreeNode, targetTeamId: string, silent = false): Promise<string | null> => {
      const isCollection = node.kind === "collection";
      const rpcName = isCollection ? "create_collection_team_request" : "create_file_team_request";
      const rpcArgs = isCollection
        ? {
            p_collection_id: node.entityId,
            p_target_team_id: targetTeamId,
            p_source_team_id: node.teamId ?? null,
          }
        : {
            p_file_id: node.entityId,
            p_target_team_id: targetTeamId,
            p_source_team_id: node.teamId ?? null,
          };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: rpcError } = await (supabase.rpc as any)(rpcName, rpcArgs);

      if (rpcError) {
        toast.error(rpcError.message || "Failed to send move request.");
        return null;
      }

      const payload = (data ?? {}) as {
        request_id?: string;
        owner_recipients?: Array<{ user_id: string; email: string; name: string }>;
      };

      const recipients = payload.owner_recipients ?? [];
      if (recipients.length > 0) {
        const targetTeam = teams.find((team) => team.id === targetTeamId);
        const requestUrl =
          typeof window !== "undefined"
            ? `${window.location.origin}/files/requests?requestId=${encodeURIComponent(payload.request_id ?? "")}`
            : "/files/requests";
        const kindLabel = isCollection ? "Collection" : "File";

        for (const recipient of recipients) {
          void supabase.functions.invoke("send-email", {
            body: {
              header: `${kindLabel} move request: ${node.name}`,
              content:
                `A ${kindLabel.toLowerCase()} team move request has been submitted.\n\n` +
                `${kindLabel}: ${node.name}\n` +
                `Target team: ${targetTeam?.name ?? targetTeamId}\n\n` +
                `Open request: ${requestUrl}`,
              useWoodsTemplate: true,
              templateData: {
                preheader: `${kindLabel} move request for “${node.name}”`,
                title: `${kindLabel} Move Request`,
                intro: `A user has requested to move a ${kindLabel.toLowerCase()} to your team.`,
                ctaLabel: "Review Request",
                ctaUrl: requestUrl,
                footerNote:
                  `This automated notification was sent by Woods AI when a ${kindLabel.toLowerCase()} team move was requested.`,
              },
              users: [{ email: recipient.email, name: recipient.name }],
            },
          });
        }
      }

      if (!silent) {
        toast.success(
          `Move request sent for “${node.name}”. Awaiting team owner approval.`
        );
      }

      return payload.request_id ?? null;
    },
    [teams]
  );

  const moveFilesToTeam = useCallback(
    async (fileNodes: FileManagerTreeNode[], targetTeamId: string, label: string) => {
      if (fileNodes.length === 0) return;
      const targetTeam = teams.find((team) => team.id === targetTeamId);
      const requestIds: string[] = [];

      for (const node of fileNodes) {
        const requestId = await requestTeamMove(node, targetTeamId, true);
        if (requestId) requestIds.push(requestId);
      }

      if (requestIds.length === 0) return;

      toast.success(
        `${label} — move request sent to ${targetTeam?.name ?? "the team"}. Awaiting owner approval.`,
        {
          action: {
            label: "Undo",
            onClick: () => {
              void (async () => {
                for (const requestId of requestIds) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const { error: cancelError } = await (supabase.rpc as any)(
                    "cancel_file_team_request",
                    { p_request_id: requestId }
                  );
                  if (cancelError) {
                    toast.error(cancelError.message || "Could not withdraw the move request.");
                    return;
                  }
                }
                toast.success("Move request withdrawn.");
                void refresh();
              })();
            },
          },
        }
      );
      void refresh();
    },
    [teams, requestTeamMove, refresh]
  );

  // ─── Within-team moves (immediate, reversible) ───────────────────────────────

  /** Re-links one file between a team root and a collection inside the same team. */
  const relinkFile = useCallback(
    async (
      fileId: string,
      teamId: string,
      fromCollectionId: string | null,
      toCollectionId: string | null
    ) => {
      if (fromCollectionId === toCollectionId) return;

      if (fromCollectionId) {
        const { error: delError } = await supabase
          .from("collection_files")
          .delete()
          .eq("file_id", fileId)
          .eq("collection_id", fromCollectionId);
        if (delError) throw delError;
      } else {
        const { error: delError } = await supabase
          .from("file_teams")
          .delete()
          .eq("file_id", fileId)
          .eq("team_id", teamId);
        if (delError) throw delError;
      }

      if (toCollectionId) {
        const { error: insError } = await supabase
          .from("collection_files")
          .insert({ file_id: fileId, collection_id: toCollectionId, user_id: currentUserId });
        if (insError) throw insError;
      } else {
        const { error: insError } = await supabase
          .from("file_teams")
          .insert({ file_id: fileId, team_id: teamId, user_id: currentUserId });
        if (insError) throw insError;
      }
    },
    [currentUserId]
  );

  const moveFilesToCollection = useCallback(
    async (fileNodes: FileManagerTreeNode[], toCollectionId: string | null, label: string, destination: string) => {
      const movable = fileNodes.filter((file) => (file.collectionId ?? null) !== toCollectionId);
      if (movable.length === 0) return;

      const undoSteps = movable.map((file) => ({
        fileId: file.entityId,
        teamId: file.teamId,
        fromCollectionId: file.collectionId ?? null,
      }));

      try {
        for (const file of movable) {
          await relinkFile(file.entityId, file.teamId, file.collectionId ?? null, toCollectionId);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Move failed.");
        void refresh();
        return;
      }

      toast.success(`${label} moved to ${destination}.`, {
        action: {
          label: "Undo",
          onClick: () => {
            void (async () => {
              try {
                for (const step of undoSteps) {
                  await relinkFile(step.fileId, step.teamId, toCollectionId, step.fromCollectionId);
                }
                toast.success("Move undone.");
              } catch (err) {
                toast.error(err instanceof Error ? err.message : "Could not undo the move.");
              }
              void refresh();
            })();
          },
        },
      });
      void refresh();
    },
    [relinkFile, refresh]
  );

  // ─── Drag & drop ─────────────────────────────────────────────────────────────

  const [drag, setDrag] = useState<KbDragPayload | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const handleDragStartFiles = useCallback(
    (files: FileManagerTreeNode[]) => {
      if (files.length === 0) return;
      const label =
        files.length === 1
          ? files[0].name
          : `${files.length} copies of ${files[0].name}`;
      setDrag({
        nodeIds: files.map((file) => file.id),
        label,
        teamId: files[0].teamId,
        canMoveTeam: files.every(canMoveTeam),
      });
    },
    [canMoveTeam]
  );

  const handleDragEndFiles = useCallback(() => {
    setDrag(null);
    setDropTargetId(null);
  }, []);

  const draggedFiles = useCallback(
    (payload: KbDragPayload) =>
      payload.nodeIds
        .map((id) => nodeIndex.get(id))
        .filter((node): node is FileManagerTreeNode => !!node && node.kind === "file"),
    [nodeIndex]
  );

  const handleDropOnTeam = useCallback(
    (teamNode: FileManagerTreeNode) => {
      const payload = drag;
      handleDragEndFiles();
      if (!payload) return;
      void moveFilesToTeam(draggedFiles(payload), teamNode.entityId, payload.label);
    },
    [drag, draggedFiles, handleDragEndFiles, moveFilesToTeam]
  );

  const handleDropOnCollection = useCallback(
    (collectionNode: FileManagerTreeNode) => {
      const payload = drag;
      handleDragEndFiles();
      if (!payload) return;
      void moveFilesToCollection(
        draggedFiles(payload),
        collectionNode.entityId,
        payload.label,
        collectionNode.name
      );
    },
    [drag, draggedFiles, handleDragEndFiles, moveFilesToCollection]
  );

  const handleDropOnTeamRoot = useCallback(() => {
    const payload = drag;
    handleDragEndFiles();
    if (!payload || !selectedTeam) return;
    void moveFilesToCollection(draggedFiles(payload), null, payload.label, selectedTeam.name);
  }, [drag, draggedFiles, handleDragEndFiles, moveFilesToCollection, selectedTeam]);

  // ─── Bulk actions ────────────────────────────────────────────────────────────

  const toggleFileSelection = useCallback((id: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectedFileNodes = useMemo(
    () =>
      [...selectedFileIds]
        .map((id) => nodeIndex.get(id))
        .filter((node): node is FileManagerTreeNode => !!node && node.kind === "file"),
    [selectedFileIds, nodeIndex]
  );

  const canDeleteSelection = useMemo(
    () => selectedFileNodes.length > 0 && selectedFileNodes.every(canDeleteFile),
    [selectedFileNodes, canDeleteFile]
  );

  const handleBulkDelete = useCallback(async () => {
    if (selectedFileNodes.length === 0) return;
    if (!window.confirm(`Delete ${pluralise(selectedFileNodes.length, "file")}? This cannot be undone.`)) return;
    let deletedCount = 0;
    for (const node of selectedFileNodes) {
      const { data: deleted, error: delError } = await supabase
        .from("files")
        .delete()
        .eq("id", node.entityId)
        .select("id");
      if (delError) toast.error(`Failed to delete "${node.name}": ${delError.message}`);
      else if (!deleted?.length) toast.error(`You don't have permission to delete "${node.name}".`);
      else deletedCount += 1;
    }
    setSelectedFileIds(new Set());
    if (deletedCount > 0) toast.success(`${pluralise(deletedCount, "file")} deleted.`);
    void refresh();
  }, [selectedFileNodes, refresh]);

  const handleBulkRetry = useCallback(async () => {
    if (selectedFileNodes.length === 0) return;
    for (const node of selectedFileNodes) {
      void retryFile(node).catch(() => undefined);
    }
    setSelectedFileIds(new Set());
    toast.success(`${pluralise(selectedFileNodes.length, "file")} queued for reprocessing.`);
    void refresh();
  }, [selectedFileNodes, retryFile, refresh]);

  // Starts a brand-new chat scoped to the given files/collections via the
  // myFiles tool (see home-client.tsx's ?kbFileIds=/?kbCollectionIds= deep link).
  // `label` is a short human-readable scope description shown as a pill in the
  // new chat (e.g. a file name or "3 files") — carried via ?kbLabel= since
  // ChatView never loads the underlying file/collection rows itself.
  const handleStartChat = useCallback(
    (scope: { fileIds?: string[]; collectionIds?: string[]; label: string }) => {
      const params = new URLSearchParams();
      if (scope.fileIds?.length) params.set("kbFileIds", scope.fileIds.join(","));
      if (scope.collectionIds?.length) params.set("kbCollectionIds", scope.collectionIds.join(","));
      params.set("kbLabel", scope.label);
      router.push(`/?${params.toString()}`);
    },
    [router]
  );

  const handleStartChatWithSelected = useCallback(() => {
    if (selectedFileNodes.length === 0) return;
    handleStartChat({
      fileIds: selectedFileNodes.map((node) => node.entityId),
      label:
        selectedFileNodes.length === 1
          ? selectedFileNodes[0].name
          : `${selectedFileNodes.length} files`,
    });
  }, [selectedFileNodes, handleStartChat]);

  const handleBulkMoveToTeam = useCallback(
    async (targetTeamId: string) => {
      await moveFilesToTeam(
        selectedFileNodes,
        targetTeamId,
        pluralise(selectedFileNodes.length, "file")
      );
      setSelectedFileIds(new Set());
    },
    [selectedFileNodes, moveFilesToTeam]
  );

  const handleBulkMoveToCollection = useCallback(
    async (targetNodeId: string) => {
      const target = nodeIndex.get(targetNodeId);
      if (!target) return;
      await moveFilesToCollection(
        selectedFileNodes,
        target.kind === "collection" ? target.entityId : null,
        pluralise(selectedFileNodes.length, "file"),
        target.kind === "collection" ? target.name : `${target.name} (team root)`
      );
      setSelectedFileIds(new Set());
    },
    [selectedFileNodes, nodeIndex, moveFilesToCollection]
  );

  // ─── Row actions ─────────────────────────────────────────────────────────────

  const moveTargetsFor = useCallback(
    (file: FileManagerTreeNode): KbMoveTargets => ({
      teams: teams
        .filter((team) => team.id !== file.teamId)
        .map((team) => ({ id: team.id, name: team.name })),
      collections: [
        ...(file.collectionId && selectedTeam
          ? [{ id: selectedTeam.id, name: `${selectedTeam.name} (team root)` }]
          : []),
        ...collections
          .filter((collection) => collection.collectionId !== file.collectionId)
          .map((collection) => ({ id: collection.id, name: collection.name })),
      ],
    }),
    [teams, collections, selectedTeam]
  );

  const renderRowActions = useCallback(
    (file: FileManagerTreeNode) => (
      <KbRowActions
        node={file}
        canEditMetadata={canEditFileMetadata(file)}
        canMoveTeam={canMoveTeam(file)}
        canDelete={canDeleteFile(file)}
        moveTargets={moveTargetsFor(file)}
        onDelete={handleDeleteNode}
        onPreview={handlePreviewNode}
        onRetry={handleRetryNode}
        onViewChunks={setChunksNode}
        onDownload={handleDownloadNode}
        onEditMetadata={setEditMetadataNode}
        onMoveToTeam={(node, teamId) => void moveFilesToTeam([node], teamId, node.name)}
        onMoveToCollection={(node, targetNodeId) => {
          const target = nodeIndex.get(targetNodeId);
          if (!target) return;
          void moveFilesToCollection(
            [node],
            target.kind === "collection" ? target.entityId : null,
            node.name,
            target.kind === "collection" ? target.name : `${target.name} (team root)`
          );
        }}
      />
    ),
    [
      canEditFileMetadata,
      canMoveTeam,
      canDeleteFile,
      moveTargetsFor,
      handleDeleteNode,
      handlePreviewNode,
      handleRetryNode,
      handleDownloadNode,
      moveFilesToTeam,
      moveFilesToCollection,
      nodeIndex,
    ]
  );

  const canDragFile = useCallback(
    (file: FileManagerTreeNode) => canMoveTeam(file) || collections.length > 0,
    [canMoveTeam, collections.length]
  );

  const handleToggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleShowAll = useCallback(() => {
    setSearch("");
    setStatusFilter("all");
  }, []);

  // Search hits at the team root can live in any collection, so say where.
  const searchLocationFor = useCallback(
    (file: FileManagerTreeNode) => {
      if (!file.collectionId) return selectedTeam?.name ?? null;
      const collection = collections.find((node) => node.collectionId === file.collectionId);
      return collection ? `${selectedTeam?.name ?? ""} › ${collection.name}` : null;
    },
    [collections, selectedTeam]
  );

  const busy = loading || teamsLoading;

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-background">
      <ChunksDialog
        node={chunksNode}
        open={!!chunksNode}
        onOpenChange={(open) => { if (!open) setChunksNode(null); }}
      />
      <RenameCollectionDialog
        node={renameNode}
        open={!!renameNode}
        onOpenChange={(open) => { if (!open) setRenameNode(null); }}
        onSubmit={handleRenameCollection}
      />
      <CreateCollectionDialog
        open={createCollectionOpen}
        onOpenChange={setCreateCollectionOpen}
        onSubmit={handleCreateCollection}
      />
      <MoveTeamDialog
        node={moveRequestNode}
        teams={teams}
        open={!!moveRequestNode}
        onOpenChange={(open) => { if (!open) setMoveRequestNode(null); }}
        onSubmit={async (node, targetTeamId) => { await requestTeamMove(node, targetTeamId); }}
      />
      <BulkMoveTeamDialog
        count={selectedFileIds.size}
        currentTeamId={selectedTeam?.teamId ?? null}
        teams={teams}
        open={bulkMoveOpen}
        onOpenChange={setBulkMoveOpen}
        onSubmit={handleBulkMoveToTeam}
      />
      <BulkMoveCollectionDialog
        count={selectedFileIds.size}
        collections={collections}
        teamNodeId={selectedTeam?.id ?? null}
        open={bulkMoveCollectionOpen}
        onOpenChange={setBulkMoveCollectionOpen}
        onSubmit={handleBulkMoveToCollection}
      />
      <AddTextDialog open={addTextOpen} onOpenChange={setAddTextOpen} onSubmit={handleAddText} />
      {editMetadataNode && (
        <EditFileMetadataDialog
          fileId={editMetadataNode.entityId}
          open
          onOpenChange={(open) => { if (!open) setEditMetadataNode(null); }}
          onSuccess={() => { void refresh(); }}
          documentDate={editMetadataNode.documentDate}
          versionLabel={editMetadataNode.versionLabel}
          docCreated={editMetadataNode.docCreated}
          docLastModified={editMetadataNode.docLastModified}
          revision={editMetadataNode.revision}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        aria-hidden
        onChange={(event) => {
          if (event.target.files) void handleFiles(event.target.files);
          event.target.value = "";
        }}
      />

      <PageHeader
        variant="collection"
        title="Library"
        onBack={onBack}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search files…"
        searchAriaLabel="Search files by name"
        actions={
          <div className="ml-auto flex items-center gap-1">
            {onLearn ? (
              <LearnButton label="Learn about the Library" onClick={onLearn} />
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Add text to the Library"
                  className="size-9 text-muted-foreground"
                  onClick={() => setAddTextOpen(true)}
                  disabled={busy || !uploadTarget || isUploading}
                >
                  <FileTextIcon className="size-4" size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Add text</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  className="ml-1 gap-1.5"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy || !uploadTarget || isUploading}
                >
                  <UploadIcon className="size-4" size={16} />
                  <span className="hidden sm:inline">{isUploading ? "Uploading…" : "Upload"}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {uploadTarget ? `Upload to “${uploadTarget.name}”` : "Upload files"}
              </TooltipContent>
            </Tooltip>
          </div>
        }
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
      {/* Scroll region — also the drop zone for files dragged in from the desktop */}
      <div
        className="relative min-w-0 flex-1 overflow-y-auto"
        onDragEnter={(event) => {
          if (!event.dataTransfer.types.includes("Files")) return;
          event.preventDefault();
          dragCounterRef.current += 1;
          if (dragCounterRef.current === 1) setIsDraggingExternal(true);
        }}
        onDragOver={(event) => {
          if (event.dataTransfer.types.includes("Files")) event.preventDefault();
        }}
        onDragLeave={() => {
          if (dragCounterRef.current === 0) return;
          dragCounterRef.current -= 1;
          if (dragCounterRef.current === 0) setIsDraggingExternal(false);
        }}
        onDrop={(event) => {
          dragCounterRef.current = 0;
          setIsDraggingExternal(false);
          if (!event.dataTransfer.files.length) return;
          event.preventDefault();
          void handleFiles(event.dataTransfer.files);
        }}
      >
        {isDraggingExternal && (
          <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-primary/10 backdrop-blur-[2px]">
            <UploadIcon className="size-10 text-primary" size={40} />
            <p className="text-base font-medium text-primary">
              Drop files to upload to &ldquo;{uploadTarget?.name ?? "here"}&rdquo;
            </p>
            <p className="text-sm text-primary/70">Release to add files</p>
          </div>
        )}

        <div className="mx-auto w-full max-w-4xl space-y-8 px-4 pb-10 pt-2 sm:px-10">
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Unable to load Library</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <TeamTabs
            teams={nodes}
            selectedTeamId={resolvedTeamNodeId}
            onSelect={setSelectedTeamNodeId}
            loading={teamsLoading}
            drag={drag}
            dropTargetId={dropTargetId}
            onDropTargetChange={setDropTargetId}
            onDropFiles={handleDropOnTeam}
          />

          {selectedTeam ? (
            <>
              {selectedCollection ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Breadcrumb className="min-w-0">
                    <BreadcrumbList className="text-lg sm:gap-2">
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <button
                            type="button"
                            onClick={() => setSelectedCollectionId(null)}
                            onDragOver={(event) => {
                              if (drag?.teamId !== selectedTeam.entityId) return;
                              event.preventDefault();
                              event.dataTransfer.dropEffect = "move";
                              setDropTargetId(selectedTeam.id);
                            }}
                            onDragLeave={() => setDropTargetId(null)}
                            onDrop={(event) => {
                              if (drag?.teamId !== selectedTeam.entityId) return;
                              event.preventDefault();
                              handleDropOnTeamRoot();
                            }}
                            className={cn(
                              "rounded-md px-1 font-medium",
                              drag?.teamId === selectedTeam.entityId &&
                                "outline-dashed outline-1 outline-primary/50",
                              dropTargetId === selectedTeam.id && "bg-primary/15 ring-2 ring-primary"
                            )}
                          >
                            {selectedTeam.name}
                          </button>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem className="min-w-0">
                        <BreadcrumbPage className="flex min-w-0 items-center gap-2 font-semibold">
                          <FolderOpenIcon className="size-5 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="truncate">{selectedCollection.name}</span>
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={selectedCollection.fileCount === 0}
                      onClick={() =>
                        handleStartChat({
                          collectionIds: [selectedCollection.entityId],
                          label: selectedCollection.name,
                        })
                      }
                    >
                      <MessageSquarePlusIcon className="size-4" size={16} />
                      <span className="hidden sm:inline">Start chat</span>
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          aria-label={`More actions for ${selectedCollection.name}`}
                        >
                          <MoreHorizontalIcon className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => setRenameNode(selectedCollection)}>
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setMoveRequestNode(selectedCollection)}>
                          Change team
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => void handleDeleteNode(selectedCollection)}
                        >
                          <DeleteIcon className="size-4" size={16} />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ) : !isSearching ? (
                <section className="space-y-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Collections
                    </h2>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {pluralise(collections.length, "collection")}
                    </p>
                  </div>
                  <CollectionGrid
                    collections={collections}
                    onOpen={openCollection}
                    onCreate={() => setCreateCollectionOpen(true)}
                    canCreate={!busy}
                    onStartChat={(collection) =>
                      handleStartChat({ collectionIds: [collection.entityId], label: collection.name })
                    }
                    onRename={setRenameNode}
                    onChangeTeam={setMoveRequestNode}
                    onDelete={(collection) => void handleDeleteNode(collection)}
                    drag={drag}
                    dropTargetId={dropTargetId}
                    onDropTargetChange={setDropTargetId}
                    onDropFiles={handleDropOnCollection}
                  />
                </section>
              ) : null}

              <section className="space-y-4">
                {!selectedCollection ? (
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {isSearching
                        ? `Results in ${selectedTeam.name}`
                        : collections.length > 0
                          ? "Files not in a collection"
                          : "Files"}
                    </h2>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {isSearching
                        ? pluralise(visibleFiles.length, "match", "matches")
                        : pluralise(scopedFiles.length, "file")}
                    </p>
                  </div>
                ) : null}
                <KbStatusBar
                  counts={statusCounts}
                  active={statusFilter}
                  onChange={setStatusFilter}
                  onRetryFailed={() => void handleRetryFailed()}
                  retrying={retryingFailed}
                />

                {selectedFileNodes.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
                    <p className="text-xs text-muted-foreground">
                      {pluralise(selectedFileNodes.length, "file")} selected
                    </p>
                    <div className="ml-auto flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label="Start chat with selected files"
                        onClick={handleStartChatWithSelected}
                      >
                        <MessageSquarePlusIcon className="size-4" size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label="Retry selected files"
                        onClick={() => void handleBulkRetry()}
                      >
                        <RefreshCWIcon className="size-4" size={16} />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            aria-label="Move selected files"
                          >
                            <FolderInput className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setBulkMoveCollectionOpen(true)}>
                            Move to collection
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setBulkMoveOpen(true)}>
                            Move to team
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {canDeleteSelection ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8 text-destructive hover:text-destructive"
                          aria-label="Delete selected files"
                          onClick={() => void handleBulkDelete()}
                        >
                          <DeleteIcon className="size-4" size={16} />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <KbFilesTable
                  rows={rows}
                  resetKey={`${resolvedTeamNodeId ?? ""}|${selectedCollectionId ?? ""}|${statusFilter}|${trimmedSearch}|${sort?.key ?? ""}${sort?.direction ?? ""}`}
                  visibleCount={visibleFiles.length}
                  totalCount={scopedFiles.length}
                  filtered={isSearching || statusFilter !== "all"}
                  onShowAll={handleShowAll}
                  sort={sort}
                  onSortChange={handleSortChange}
                  expandedGroups={expandedGroups}
                  onToggleGroup={handleToggleGroup}
                  selectedFileIds={selectedFileIds}
                  onToggleSelection={toggleFileSelection}
                  canDragFile={canDragFile}
                  onDragStartFiles={handleDragStartFiles}
                  onDragEndFiles={handleDragEndFiles}
                  renderActions={renderRowActions}
                  loading={loading}
                  onUpload={() => fileInputRef.current?.click()}
                  canUpload={!!uploadTarget && !isUploading}
                  emptyMessage={
                    selectedCollection
                      ? `${selectedCollection.name} is empty. Upload files here, or drag files onto it from ${selectedTeam.name}.`
                      : collections.length > 0
                        ? `Every file in ${selectedTeam.name} is inside a collection.`
                        : `No files in ${selectedTeam.name} yet.`
                  }
                  uploadLabel={`Upload to ${uploadTarget?.name ?? selectedTeam.name}`}
                  locationFor={isSearching && !selectedCollection ? searchLocationFor : undefined}
                  onPreview={handlePreviewNode}
                  previewingFileId={previewingFileId}
                />
              </section>
            </>
          ) : null}
        </div>
      </div>
      {preview && (
        <ProjectFilePreviewPanel
          filename={preview.filename}
          url={preview.url}
          onClose={closePreview}
        />
      )}
      </div>
    </div>
  );
}
